import React, { useState } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import apiService from '../../services/api';

interface NoShowModalProps {
    isOpen: boolean;
    onClose: () => void;
    meetingId: number;
    meetingTypeId: number;
    token: string;
    onSuccess: () => void;
}

const NO_SHOW_REASONS = [
    'Technical issue (connection / audio / video)',
    'Patient unavailable or did not respond',
    'Pre-appointment requirements not completed',
    'Unsuitable environment',
    'Parents not present (pediatric only)',
    'Other',
];

export const NoShowModal: React.FC<NoShowModalProps> = ({
    isOpen,
    onClose,
    meetingId,
    meetingTypeId,
    token,
    onSuccess,
}) => {
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [additionalDetails, setAdditionalDetails] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleReasonChange = (reason: string) => {
        setSelectedReason(reason);
        // No longer clear additional details - keep them for all reasons
        setError(null);
    };

    const handleConfirm = async () => {
        // Validate
        if (!selectedReason) {
            setError('Please select a reason');
            return;
        }

        if (selectedReason === 'Other' && !additionalDetails.trim()) {
            setError('Please provide additional details');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await apiService.updateMeetingStatus({
                token,
                meeting_id: meetingId,
                status: 'noshow',
                source: 'UI',
                meetingtype_id: meetingTypeId,
                reason_of_cancellation: '',
                cancel_reason: selectedReason,
                cancel_reason_additional_text: additionalDetails.trim(),
            });

            // Reset form
            setSelectedReason('');
            setAdditionalDetails('');

            // Call success callback
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Failed to mark meeting as no-show:', err);
            setError(err.response?.data?.message || err.message || 'Failed to mark meeting as no-show');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setSelectedReason('');
            setAdditionalDetails('');
            setError(null);
            onClose();
        }
    };

    const isConfirmDisabled = !selectedReason || (selectedReason === 'Other' && !additionalDetails.trim()) || isSubmitting;

    return (
        <ModalWrapper modalId="NoShowModal"
            isOpen={isOpen}
            onClose={handleClose}
            title="Mark as No Show"
            isLoading={isSubmitting}
            width="max-w-lg"
        >
            <div className="space-y-4">
                {/* Reason Selection - Dropdown */}
                <div>
                    <label className="block text-sm font-medium text-[#366A75] mb-2">
                        Select Reason <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={selectedReason}
                        onChange={(e) => handleReasonChange(e.target.value)}
                        className={POPUP_STYLES.input}
                        disabled={isSubmitting}
                    >
                        <option value="">-- Select a reason --</option>
                        {NO_SHOW_REASONS.map((reason) => (
                            <option key={reason} value={reason}>
                                {reason}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Additional Details - Always shown, mandatory only when "Other" is selected */}
                <div>
                    <label className="block text-sm font-medium text-[#366A75] mb-2">
                        Additional Details {selectedReason === 'Other' && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                        value={additionalDetails}
                        onChange={(e) => setAdditionalDetails(e.target.value)}
                        className={`${POPUP_STYLES.input} resize-none`}
                        rows={4}
                        placeholder={selectedReason === 'Other' ? "Please provide details about the no-show..." : "Optional additional information..."}
                        disabled={isSubmitting}
                    />
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end pt-2">
                    <button
                        onClick={handleClose}
                        className={POPUP_STYLES.button}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        className={POPUP_STYLES.button}
                        disabled={isConfirmDisabled}
                    >
                        {isSubmitting ? 'Submitting...' : 'Confirm'}
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};
